import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import useCifaStore, { RecursoFinanceiro } from 'stores/cifa/cifa.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';

interface Step2Props {
  data: {
    recursos: RecursoFinanceiro[];
    rcl: number;
  };
  onBack: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

export const formatCurrencyDisplay = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return numValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const parseCurrencyInput = (value: string): number => {
  const digitsOnly = value.replace(/\D/g, '');
  return digitsOnly ? parseFloat(digitsOnly) / 100 : 0;
};

export const getRecursoKey = (recurso: RecursoFinanceiro, index: number) => {
  // Gera uma key única baseada nos dados do recurso
  const baseKey = `${recurso.tipo}-${recurso.descricao}`.replace(/\s+/g, '-').toLowerCase();
  return `${baseKey}-${index}`;
};

export const calculateFinancialSummary = (localData: { recursos: RecursoFinanceiro[]; rcl: number }) => {
  const totalOrcado = localData.recursos.reduce((sum, item) => sum + (item.valor_orcado || 0), 0);
  const totalLiquidado = localData.recursos.reduce((sum, item) => sum + (item.valor_liquidado || 0), 0);

  const percentualExecucao = totalOrcado > 0
    ? (totalLiquidado / totalOrcado) * 100
    : 0;

  const percentualRCL = localData.rcl > 0
    ? (totalLiquidado / localData.rcl) * 100
    : 0;

  return {
    totalOrcado,
    totalLiquidado,
    percentualExecucao: parseFloat(percentualExecucao.toFixed(2)),
    percentualRCL: parseFloat(percentualRCL.toFixed(2))
  };
};

// Pure helpers for resource validation and list updates
export const validateRecurso = (item: RecursoFinanceiro | null): string | null => {
  if (!item) return 'Item inválido';
  if (!item.descricao || !item.descricao.trim()) return 'Descrição é obrigatória';
  if ((item.valor_orcado ?? 0) <= 0) return 'Valor orçado deve ser positivo';
  if ((item.valor_liquidado ?? 0) < 0) return 'Valor liquidado não pode ser negativo';
  if ((item.valor_liquidado ?? 0) > (item.valor_orcado ?? 0)) return 'Valor liquidado não pode ser maior que o valor orçado';
  return null;
};

export const saveResourceList = (resources: RecursoFinanceiro[], item: RecursoFinanceiro, index: number | null) => {
  const updated = [...resources];
  if (index !== null && index >= 0 && index < updated.length) {
    updated[index] = item;
  } else {
    updated.push(item);
  }
  return updated;
};

export const deleteResourceList = (resources: RecursoFinanceiro[], index: number) => resources.filter((_, i) => i !== index);

export const computeExecutionPercent = (valor_orcado?: number | null, valor_liquidado?: number | null) => {
  const orcado = valor_orcado ?? 0;
  const liquidado = valor_liquidado ?? 0;
  if (orcado <= 0) return 0;
  return parseFloat(((liquidado / orcado) * 100).toFixed(2));
};

export const getIncludeCifaRoute = (ano: number) => {
  return ROUTES[`INCLUDE_CIFA_${ano}` as keyof typeof ROUTES] as string | undefined;
};

// Mapper to convert store `cifa` shape into localData used by the component
export const mapCifaToLocalData = (cifa: any) => ({
  recursos: cifa?.recursos_financeiros_aplicados_detalhamento || [],
  rcl: typeof cifa?.receita_corrente_liquida_2025 === 'number' ? cifa.receita_corrente_liquida_2025 : 0
});

// Presentational pure components exported for testing (no hooks inside)
export const RenderSummary = ({ financialSummary }: { financialSummary: ReturnType<typeof calculateFinancialSummary> }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {[
      { title: 'Total Orçado', value: financialSummary.totalOrcado, color: '#e3f2fd' },
      { title: 'Total Liquidado', value: financialSummary.totalLiquidado, color: '#e8f5e9' },
      { title: '% Execução', value: financialSummary.percentualExecucao, suffix: '%', color: '#fff3e0' },
      { title: '% da RCL', value: financialSummary.percentualRCL, suffix: '%', color: '#f3e5f5' }
    ].map((card) => (
      <Grid item xs={12} md={3} key={card.title.replace(/\s+/g, '-').toLowerCase()}>
        <Paper elevation={3} sx={{ p: 2, textAlign: 'center', backgroundColor: card.color }}>
          <Typography variant="subtitle2">{card.title}</Typography>
          <Typography variant="h6" fontWeight="bold">
            {card.suffix ? `${card.value}${card.suffix}` : formatCurrencyDisplay(card.value as any)}
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

export const RenderResourcesTable = ({ localData, onAddClick, onEditClick, onDeleteClick, parentLoading, isSubmitting }: {
  localData: { recursos: RecursoFinanceiro[]; rcl: number };
  onAddClick: () => void;
  onEditClick: (i: number) => void;
  onDeleteClick: (i: number) => void;
  parentLoading?: boolean;
  isSubmitting?: boolean;
}) => (
  <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h5" fontWeight="bold">Recursos Financeiros</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={onAddClick} disabled={isSubmitting || parentLoading}>Adicionar</Button>
    </Box>

    <TableContainer>
      <Table>
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            <TableCell><strong>Tipo</strong></TableCell>
            <TableCell><strong>Descrição</strong></TableCell>
            <TableCell align="right"><strong>Orçado (R$)</strong></TableCell>
            <TableCell align="right"><strong>Liquidado (R$)</strong></TableCell>
            <TableCell align="right"><strong>% Exec.</strong></TableCell>
            <TableCell align="center"><strong>Ações</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {localData.recursos.map((item, index) => {
            const executionPercent = item.valor_orcado > 0 ? ((item.valor_liquidado / item.valor_orcado) * 100).toFixed(2) : '0.00';
            return (
              <TableRow key={getRecursoKey(item, index)} hover>
                <TableCell>{item.tipo}</TableCell>
                <TableCell>{item.descricao}</TableCell>
                <TableCell align="right">{formatCurrencyDisplay(item.valor_orcado)}</TableCell>
                <TableCell align="right">{formatCurrencyDisplay(item.valor_liquidado)}</TableCell>
                <TableCell align="right">{executionPercent}%</TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => onEditClick(index)} disabled={isSubmitting || parentLoading} color="primary"><EditIcon /></IconButton>
                  <IconButton onClick={() => onDeleteClick(index)} disabled={isSubmitting || parentLoading} color="error"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {localData.recursos.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">Nenhum recurso cadastrado</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

export const RenderRCLSection = ({ localData, financialSummary, onRCLChange }: { localData: { recursos: RecursoFinanceiro[]; rcl: number }; financialSummary: ReturnType<typeof calculateFinancialSummary>; onRCLChange: (v: string) => void }) => (
  <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
    <Typography variant="h6" gutterBottom fontWeight="bold">Receita Corrente Líquida (RCL) 2025</Typography>
    <TextField label="Valor Total da RCL" value={localData.rcl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} onChange={(e) => onRCLChange(e.target.value)} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, inputProps: { inputMode: 'numeric' } }} />
    <Typography variant="body2" color="text.secondary" mt={1}>{financialSummary.percentualRCL}% da RCL investido</Typography>
  </Paper>
);

export const RenderEditDialog = ({ editItem, editIndex, onClose, onChange, onSave, parentLoading, isSubmitting }: {
  editItem: RecursoFinanceiro | null;
  editIndex: number | null;
  onClose: () => void;
  onChange: (partial: Partial<RecursoFinanceiro>) => void;
  onSave: () => void;
  parentLoading?: boolean;
  isSubmitting?: boolean;
}) => (
  <Dialog open={!!editItem} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{editIndex !== null ? 'Editar Recurso' : 'Adicionar Recurso'}</DialogTitle>
    <DialogContent>
      <Stack spacing={3} sx={{ mt: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Tipo *</InputLabel>
          <Select value={editItem?.tipo ?? ''} label="Tipo *" onChange={(e) => onChange({ tipo: e.target.value as any })}>
            {['Programa', 'Projeto', 'Ação/Atividade'].map((tipo) => (<MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>))}
          </Select>
        </FormControl>

        <TextField label="Descrição *" value={editItem?.descricao ?? ''} onChange={(e) => onChange({ descricao: e.target.value.toLocaleUpperCase() })} fullWidth multiline rows={3} />

        <TextField label="Valor Orçado *" value={editItem?.valor_orcado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? ''} onChange={(e) => onChange({ valor_orcado: parseCurrencyInput(e.target.value) })} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, inputProps: { inputMode: 'numeric' } }} />

        <TextField label="Valor Liquidado" value={editItem?.valor_liquidado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? ''} onChange={(e) => onChange({ valor_liquidado: parseCurrencyInput(e.target.value) })} fullWidth InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, inputProps: { inputMode: 'numeric' } }} />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancelar</Button>
      <Button variant="contained" onClick={onSave} disabled={isSubmitting || parentLoading}>{editIndex !== null ? 'Atualizar' : 'Adicionar'}</Button>
    </DialogActions>
  </Dialog>
);

export const RenderDeleteDialog = ({ open, onClose, onConfirm }: { open: boolean; onClose: () => void; onConfirm: () => void }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Confirmar Exclusão</DialogTitle>
    <DialogContent>
      <Typography>Tem certeza que deseja excluir este recurso?</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancelar</Button>
      <Button onClick={onConfirm} variant="contained" color="error">Confirmar</Button>
    </DialogActions>
  </Dialog>
);

export const RenderFeedbackSnackbar = ({ open, message, severity, onClose }: { open: boolean; message: string; severity: 'success' | 'error'; onClose: () => void }) => (
  <Snackbar open={open} autoHideDuration={6000} onClose={onClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
    <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>{message}</Alert>
  </Snackbar>
);

export const RenderNavigationButtons = ({ onBack, onConfirm, disabled, isSubmitting }: { onBack: () => void; onConfirm: () => void; disabled?: boolean; isSubmitting?: boolean }) => (
  <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
    <Button variant="outlined" onClick={onBack} sx={{ minWidth: '120px' }} disabled={isSubmitting || disabled}>Voltar</Button>
    <Button variant="contained" onClick={onConfirm} sx={{ minWidth: '120px' }} disabled={isSubmitting || disabled} endIcon={isSubmitting ? <CircularProgress size={24} /> : null}>{isSubmitting ? 'Salvando...' : 'Confirmar'}</Button>
  </Stack>
);

const Step2: React.FC<Step2Props> = ({
  data,
  onBack,
  onConfirm,
  loading: parentLoading,
}) => {
  const { setPartialForm, cifa } = useCifaStore();
  const [localData, setLocalData] = useState(data);
  const [editItem, setEditItem] = useState<RecursoFinanceiro | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const match = RegExp(/cifa-(\d{4})/).exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  useEffect(() => {
    setLocalData(mapCifaToLocalData(cifa));
  }, [cifa]);




  // Financial summary calculations
  const financialSummary = useMemo(() => calculateFinancialSummary(localData), [localData]);

  const handleAddItem = () => {
    setEditItem({
      tipo: 'Programa',
      descricao: '',
      valor_orcado: 0,
      valor_liquidado: 0,
    });
    setEditIndex(null);
  };

  const handleEditItem = (index: number) => {
    setEditItem({ ...localData.recursos[index] });
    setEditIndex(index);
  };

  const handleSaveItem = () => {
    if (!editItem) return;

    if (!editItem.descricao.trim()) {
      showFeedback('Descrição é obrigatória', 'error');
      return;
    }

    if (editItem.valor_orcado <= 0) {
      showFeedback('Valor orçado deve ser positivo', 'error');
      return;
    }

    if (editItem.valor_liquidado < 0) {
      showFeedback('Valor liquidado não pode ser negativo', 'error');
      return;
    }

    if (editItem.valor_liquidado > editItem.valor_orcado) {
      showFeedback('Valor liquidado não pode ser maior que o valor orçado', 'error');
      return;
    }

    const updatedResources = [...localData.recursos];

    if (editIndex !== null) {
      updatedResources[editIndex] = editItem;
    } else {
      updatedResources.push(editItem);
    }

    setLocalData({
      ...localData,
      recursos: updatedResources
    });

    setPartialForm('recursos_financeiros_aplicados_detalhamento', updatedResources);

    showFeedback(editIndex !== null ? 'Item atualizado' : 'Item adicionado', 'success');
    setEditItem(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex === null) return;

    const updatedResources = localData.recursos.filter((_, idx) => idx !== deleteIndex);
    setLocalData({
      ...localData,
      recursos: updatedResources
    });

    setPartialForm('recursos_financeiros_aplicados_detalhamento', updatedResources);

    showFeedback('Item removido', 'success');
    setDeleteIndex(null);
  };

  const handleRCLChange = (value: string) => {
    const newRcl = parseCurrencyInput(value);
    setLocalData(prev => ({ ...prev, rcl: newRcl }));
    setPartialForm('receita_corrente_liquida_2025', newRcl);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Garantir que todos os dados estejam atualizados antes de confirmar
      await new Promise(resolve => setTimeout(resolve, 100));

      await onConfirm();

      showFeedback('Dados salvos com sucesso!', 'success');
      setTimeout(() => {
        navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
      }, 1500);
    } catch (error) {
      console.error('Submission error:', error);
      showFeedback('Erro ao salvar os dados', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showFeedback = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeFeedback = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1152px', margin: '0 auto' }}>
      <RenderSummary financialSummary={financialSummary} />

      <RenderResourcesTable localData={localData} onAddClick={handleAddItem} onEditClick={handleEditItem} onDeleteClick={(i) => setDeleteIndex(i)} parentLoading={parentLoading} isSubmitting={isSubmitting} />

      <RenderRCLSection localData={localData} financialSummary={financialSummary} onRCLChange={handleRCLChange} />

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editIndex !== null ? 'Editar Recurso' : 'Adicionar Recurso'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo *</InputLabel>
              <Select
                value={editItem?.tipo ?? ''}
                label="Tipo *"
                onChange={(e) => editItem && setEditItem({
                  ...editItem,
                  tipo: e.target.value as 'Programa' | 'Projeto' | 'Ação/Atividade'
                })}
              >
                {['Programa', 'Projeto', 'Ação/Atividade'].map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrição *"
              value={editItem?.descricao ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                descricao: e.target.value.toLocaleUpperCase()
              })}
              fullWidth
              multiline
              rows={3}
            />

            <TextField
              label="Valor Orçado *"
              value={editItem?.valor_orcado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                valor_orcado: parseCurrencyInput(e.target.value)
              })}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { inputMode: 'numeric' }
              }}
            />

            <TextField
              label="Valor Liquidado"
              value={editItem?.valor_liquidado.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) ?? ''}
              onChange={(e) => editItem && setEditItem({
                ...editItem,
                valor_liquidado: parseCurrencyInput(e.target.value)
              })}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputProps: { inputMode: 'numeric' }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveItem}
            disabled={isSubmitting || parentLoading}
          >
            {editIndex !== null ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir este recurso?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isSubmitting || parentLoading}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeFeedback}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Navigation Buttons */}
      <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ minWidth: '120px' }}
          disabled={isSubmitting || parentLoading}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: '120px' }}
          disabled={isSubmitting || parentLoading || localData.recursos.length === 0}
          endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
        >
          {isSubmitting ? 'Salvando...' : 'Confirmar'}
        </Button>
      </Stack>
    </Box>
  );
};

export default Step2;
